import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FileText, Sparkles, ShieldCheck, AlertTriangle, AlertOctagon, Check, X,
    Pencil, RotateCcw, Loader2, ScanLine,
} from "lucide-react";
import { GlassCard } from "@/components/ui/GlassCard";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { getAccessToken } from "@/lib/api";
import pt from "@/lib/i18n";

const FIELD_ORDER = [
    "full_name", "bi_number", "nif", "date_of_birth", "expiry_date",
    "gender", "place_of_birth", "issuing_authority", "address",
];

const FIELD_LABEL = {
    full_name: pt.idp?.fields?.full_name || "Nome Completo",
    bi_number: pt.idp?.fields?.bi_number || "Número de BI",
    nif: pt.idp?.fields?.nif || "Número de NIF",
    date_of_birth: pt.idp?.fields?.date_of_birth || "Data de Nascimento",
    expiry_date: "Data de Validade",
    gender: pt.idp?.fields?.gender || "Género",
    place_of_birth: "Naturalidade",
    issuing_authority: "Autoridade Emissora",
    address: pt.idp?.fields?.address || "Morada",
    phone: pt.idp?.fields?.phone || "Telefone",
};

const confidenceTier = (c) => {
    if (c == null) return { variant: "neutral", label: "—", icon: null };
    if (c >= 85) return { variant: "success", label: pt.idp?.confidenceHigh || "Alta confiança", icon: ShieldCheck };
    if (c >= 60) return { variant: "warning", label: pt.idp?.confidenceMed || "Verificar", icon: AlertTriangle };
    return { variant: "error", label: pt.idp?.confidenceLow || "Requere revisão", icon: AlertOctagon };
};

const Typewriter = ({ text }) => {
    const [shown, setShown] = useState("");
    useEffect(() => {
        setShown("");
        if (!text) return;
        let i = 0;
        const id = setInterval(() => {
            i += 1;
            setShown(text.slice(0, i));
            if (i >= text.length) clearInterval(id);
        }, 22);
        return () => clearInterval(id);
    }, [text]);
    return <span>{shown}</span>;
};

const FieldCard = ({ name, label, value, confidence, corrected, onEdit, editing, draft, onDraftChange, onSave, onCancel }) => {
    const tier = confidenceTier(confidence);
    const Icon = tier.icon;
    if (!value && !editing) {
        return (
            <div
                className="rounded-xl px-4 py-3 border-2 border-dashed"
                style={{ borderColor: "var(--copper-glow)", background: "var(--bg-surface)" }}
                data-testid={`field-empty-${name}`}
            >
                <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)]">{label}</p>
                <p className="font-mono text-sm text-[color:var(--slate-light)] mt-1">
                    <span className="animate-pulse">{pt.common.idpLoading}</span>
                </p>
            </div>
        );
    }
    return (
        <div
            className="rounded-xl px-4 py-3 relative glass-card"
            style={{
                borderColor: tier.variant === "success" ? "rgba(45,122,79,0.45)" : tier.variant === "warning" ? "var(--copper-glow)" : "rgba(192,57,43,0.4)",
            }}
            data-testid={`field-${name}`}
        >
            <div className="flex items-center justify-between mb-1.5">
                <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)]">{label}</p>
                <div className="flex items-center gap-1.5">
                    {corrected && <NxBadge variant="copper"><Pencil size={10} /> Corrigido</NxBadge>}
                    {Icon && <Icon size={14} className={"text-[color:var(--" + (tier.variant === "success" ? "success" : tier.variant === "warning" ? "copper" : "error") + ")]"} />}
                    <button
                        type="button"
                        onClick={onEdit}
                        className="p-1 rounded hover:bg-[color:var(--copper-subtle)]"
                        data-testid={`field-edit-${name}`}
                        title="Editar"
                    >
                        <Pencil size={12} className="text-[color:var(--slate-mid)]" />
                    </button>
                </div>
            </div>
            {editing ? (
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={draft}
                        onChange={(e) => onDraftChange(e.target.value)}
                        className="glass-input flex-1"
                        autoFocus
                        data-testid={`field-input-${name}`}
                    />
                    <button type="button" onClick={onSave} className="p-1.5 rounded-md bg-[color:var(--success)] text-white" data-testid={`field-save-${name}`}><Check size={14} /></button>
                    <button type="button" onClick={onCancel} className="p-1.5 rounded-md bg-[color:var(--bg-surface)] border border-[color:var(--glass-border)]"><X size={14} /></button>
                </div>
            ) : (
                <p className="font-mono text-sm text-[color:var(--slate-deep)]" data-testid={`field-value-${name}`}>
                    <Typewriter text={String(value)} />
                </p>
            )}
            {confidence != null && (
                <div className="mt-2 h-1 w-full rounded-full bg-[color:var(--bg-surface)] overflow-hidden">
                    <div
                        className="h-full"
                        style={{
                            width: `${confidence}%`,
                            background:
                                tier.variant === "success" ? "var(--success)" : tier.variant === "warning" ? "var(--copper)" : "var(--error)",
                        }}
                    />
                </div>
            )}
            {confidence != null && (
                <p className="font-mono text-[10px] text-[color:var(--slate-light)] mt-1">{confidence}% · {tier.label}</p>
            )}
        </div>
    );
};

const Timer = ({ startedAt, completedAt }) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (completedAt) return;
        const id = setInterval(() => setNow(Date.now()), 200);
        return () => clearInterval(id);
    }, [completedAt]);
    const elapsed = Math.max(0, ((completedAt || now) - startedAt) / 1000);
    const pct = Math.min(100, (elapsed / 90) * 100);
    const ok = elapsed <= 90;
    return (
        <div className="flex items-center gap-3" data-testid="onboarding-timer-ring">
            <div className="relative w-14 h-14">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="var(--glass-border)" strokeWidth="3" />
                    <circle
                        cx="18" cy="18" r="15" fill="none"
                        stroke={ok ? "var(--copper)" : "var(--error)"}
                        strokeWidth="3"
                        strokeDasharray={`${pct * 0.94} 100`}
                        strokeLinecap="round"
                        style={{ transition: "stroke-dasharray 0.2s linear" }}
                    />
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                    <span className="font-mono text-[11px] text-[color:var(--slate-deep)] font-medium">{elapsed.toFixed(0)}s</span>
                </div>
            </div>
            <div>
                <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)]">{pt.common.session}</p>
                <p className="font-display text-xs text-[color:var(--slate-deep)] mt-0.5">
                    {completedAt ? `${pt.common.completed} (${elapsed.toFixed(0)}s)` : `${pt.common.waiting}…`}
                </p>
            </div>
        </div>
    );
};

const DocumentExtractionPanel = ({ document: doc, onComplete, onCancel }) => {
    const [fields, setFields] = useState({}); // { name: {value, confidence, corrected} }
    const [fraudFlags, setFraudFlags] = useState([]);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState("");
    const [stage, setStage] = useState("idle"); // idle | scanning | streaming | complete | error
    const [error, setError] = useState("");
    const [startedAt] = useState(Date.now());
    const [completedAt, setCompletedAt] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const esRef = useRef(null);

    const startExtraction = () => {
        setFields({});
        setFraudFlags([]);
        setStage("scanning");
        setError("");
        setCompletedAt(null);
        const token = getAccessToken();
        const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
        const url = `${apiBase}/documents/${doc.id}/extraction-stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        esRef.current = es;
        es.addEventListener("start", () => setStage("scanning"));
        es.addEventListener("stage", (ev) => {
            try {
                const d = JSON.parse(ev.data);
                if (d.stage === "claude") setStage("streaming");
            } catch {}
        });
        es.addEventListener("field", (ev) => {
            try {
                const d = JSON.parse(ev.data);
                setFields((prev) => ({
                    ...prev,
                    [d.name]: { value: d.value, confidence: d.confidence, corrected: false },
                }));
            } catch {}
        });
        es.addEventListener("fraud_flag", (ev) => {
            try {
                const d = JSON.parse(ev.data);
                setFraudFlags((prev) => [...prev, d]);
            } catch {}
        });
        es.addEventListener("complete", () => {
            setStage("complete");
            setCompletedAt(Date.now());
            es.close();
        });
        es.addEventListener("error", (ev) => {
            // Distinguish server-sent error events (have data) from native disconnects (no data)
            if (ev?.data) {
                try {
                    const d = JSON.parse(ev.data);
                    setError(d.message || pt.idp?.extractionFailed || "Falha na extracção.");
                } catch {
                    setError(pt.idp?.extractionFailed || "Falha na extracção.");
                }
                setStage("error");
                es.close();
                return;
            }
            // Native EventSource error after stream closed normally — ignore
            // unless we never received a "complete" event
            setStage((s) => {
                if (s === "complete") return s;
                setError(pt.idp?.connectionLost || "Ligação interrompida — tente novamente.");
                return "error";
            });
            es.close();
        });
    };

    useEffect(() => {
        startExtraction();
        return () => {
            if (esRef.current) esRef.current.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc.id]);

    const startEdit = (name) => {
        setEditing(name);
        setDraft(fields[name]?.value || "");
    };
    const saveEdit = () => {
        if (!editing) return;
        setFields((prev) => ({
            ...prev,
            [editing]: { value: draft, confidence: 100, corrected: true },
        }));
        setEditing(null);
        setDraft("");
    };

    const verifiedCount = useMemo(
        () => Object.entries(fields).filter(([, f]) => f.confidence >= 85 || f.corrected).length,
        [fields],
    );
    const totalFields = Object.keys(fields).length;
    const canConfirm = stage === "complete" && totalFields > 0;

    const confirm = async () => {
        setConfirming(true);
        try {
            const confirmed_data = {};
            const human_corrections = {};
            Object.entries(fields).forEach(([k, f]) => {
                confirmed_data[k] = f.value;
                if (f.corrected) human_corrections[k] = f.value;
            });
            const { apiPatch } = await import("@/lib/api");
            const result = await apiPatch(`/documents/${doc.id}/confirm-extraction`, {
                confirmed_data,
                human_corrections,
            });
            onComplete?.(result, completedAt - startedAt);
        } catch (e) {
            setError(e?.response?.data?.errors?.[0]?.message || "Falha ao confirmar.");
        } finally {
            setConfirming(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-4" data-testid="idp-panel">
            {/* LEFT — Document preview */}
            <GlassCard padding="p-6">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="font-display text-xl text-[color:var(--slate-deep)]">{doc.file_name}</h3>
                        <p className="font-mono text-xs text-[color:var(--slate-mid)] mt-0.5">{(doc.file_size / 1024).toFixed(1)} KB · {doc.document_type}</p>
                    </div>
                    {stage === "scanning" || stage === "streaming" ? (
                        <NxBadge variant="copper"><Loader2 size={12} className="animate-spin" /> IA a analisar…</NxBadge>
                    ) : stage === "complete" ? (
                        <NxBadge variant="success"><Check size={12} /> {pt.common.completed}</NxBadge>
                    ) : stage === "error" ? (
                        <NxBadge variant="error">Erro</NxBadge>
                    ) : null}
                </div>

                <div
                    className="relative rounded-2xl overflow-hidden"
                    style={{
                        background: "linear-gradient(180deg, #fffaf2 0%, #faf3e8 100%)",
                        border: "1px solid var(--glass-border)",
                        minHeight: 520,
                    }}
                >
                    {/* Document mock-up */}
                    <div className="absolute inset-0 p-7 font-mono text-[11px] text-[color:var(--slate-mid)] leading-relaxed overflow-hidden">
                        <p className="font-display text-base text-[color:var(--copper-dark)] tracking-widest text-center mb-3">{pt.common.idpRepublic}</p>
                        <p className="font-body text-xs text-center mb-5 text-[color:var(--slate-deep)] font-medium">
                            {doc.document_type === "nif" ? "CERTIFICADO NIF" : doc.document_type === "passaporte" ? "PASSAPORTE" : "BILHETE DE IDENTIDADE NACIONAL"}
                        </p>
                        <div className="space-y-1.5 mt-6">
                            {Object.entries(fields).slice(0, 8).map(([k, f]) => (
                                <div key={k} className="flex items-baseline gap-2">
                                    <span className="text-[color:var(--slate-light)] w-32 flex-shrink-0">{FIELD_LABEL[k] || k}:</span>
                                    <span className="text-[color:var(--slate-deep)] font-medium">{f.value}</span>
                                </div>
                            ))}
                            {Object.keys(fields).length === 0 && (
                                <>
                                    <div className="opacity-30 space-y-1.5">
                                        <p>Nome Completo: ████ █████████</p>
                                        <p>{pt.common.idpBiPlaceholder}</p>
                                        <p>Data de Nascimento: ████-██-██</p>
                                        <p>Morada: ████ ███████ ██████, ██████</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {/* Scanning line */}
                    {(stage === "scanning" || stage === "streaming") && (
                        <motion.div
                            initial={{ y: 0 }}
                            animate={{ y: 480 }}
                            transition={{ duration: 2.8, repeat: Infinity, ease: "linear" }}
                            className="absolute left-0 right-0 h-8 pointer-events-none"
                            style={{
                                background:
                                    "linear-gradient(180deg, transparent 0%, rgba(200,121,65,0.45) 50%, transparent 100%)",
                                boxShadow: "0 0 24px rgba(200,121,65,0.6)",
                            }}
                            data-testid="scan-line"
                        />
                    )}
                </div>
            </GlassCard>

            {/* RIGHT — Extracted fields */}
            <div className="space-y-4">
                <GlassCard padding="p-5">
                    <div className="flex items-center justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2">
                            <Sparkles size={18} className="text-[color:var(--copper)]" />
                            <h3 className="font-display text-xl text-[color:var(--slate-deep)]">{pt.common.idpExtractionTitle}</h3>
                        </div>
                        <Timer startedAt={startedAt} completedAt={completedAt} />
                    </div>
                    <p className="font-body text-xs text-[color:var(--slate-mid)]">Motor IA NexusTel · Claude Sonnet 4</p>
                </GlassCard>

                <div className="space-y-2.5" data-testid="extracted-fields">
                    {error && (
                        <div className="rounded-xl p-3 bg-[rgba(192,57,43,0.08)] border border-[rgba(192,57,43,0.25)] text-sm text-[color:var(--error)]">
                            {error}
                        </div>
                    )}
                    {FIELD_ORDER.map((name) => {
                        const f = fields[name];
                        const isEditing = editing === name;
                        return (
                            <FieldCard
                                key={name}
                                name={name}
                                label={FIELD_LABEL[name]}
                                value={f?.value}
                                confidence={f?.confidence}
                                corrected={f?.corrected}
                                editing={isEditing}
                                draft={draft}
                                onDraftChange={setDraft}
                                onEdit={() => startEdit(name)}
                                onSave={saveEdit}
                                onCancel={() => setEditing(null)}
                            />
                        );
                    })}
                </div>

                {/* Fraud flags */}
                {fraudFlags.length > 0 && (
                    <GlassCard padding="p-4" testId="fraud-flags">
                        <h4 className="font-display text-sm text-[color:var(--slate-deep)] mb-2 flex items-center gap-2">
                            <AlertTriangle size={16} className="text-[color:var(--copper-dark)]" />
                            Sinais de Alerta
                        </h4>
                        <ul className="space-y-1.5">
                            {fraudFlags.map((f, i) => (
                                <li
                                    key={i}
                                    className="text-sm rounded-lg px-3 py-2 flex items-center gap-2"
                                    style={{
                                        background: f.severity === "error" ? "rgba(192,57,43,0.08)" : "var(--copper-subtle)",
                                        color: f.severity === "error" ? "var(--error)" : "var(--copper-dark)",
                                    }}
                                    data-testid={`fraud-flag-${f.type}`}
                                >
                                    {f.severity === "error" ? <AlertOctagon size={14} /> : <AlertTriangle size={14} />}
                                    {f.message}
                                </li>
                            ))}
                        </ul>
                    </GlassCard>
                )}

                {/* Actions */}
                <GlassCard padding="p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <p className="font-mono text-xs text-[color:var(--slate-mid)]" data-testid="verified-count">
                            {verifiedCount}/{totalFields} campos verificados
                        </p>
                        <div className="h-1.5 flex-1 max-w-[160px] rounded-full bg-[color:var(--bg-surface)] overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[color:var(--copper-light)] to-[color:var(--copper)]"
                                style={{ width: totalFields ? `${(verifiedCount / totalFields) * 100}%` : "0%" }}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <NxButton
                            variant="secondary"
                            onClick={() => startExtraction()}
                            disabled={stage === "scanning" || stage === "streaming"}
                            icon={<RotateCcw size={14} />}
                            testId="restart-extraction"
                        >
                            Reiniciar
                        </NxButton>
                        {onCancel && (
                            <NxButton variant="secondary" onClick={onCancel} testId="cancel-extraction">
                                Voltar
                            </NxButton>
                        )}
                        <NxButton
                            variant="copper"
                            onClick={confirm}
                            disabled={!canConfirm}
                            loading={confirming}
                            icon={<ShieldCheck size={14} />}
                            testId="confirm-extraction"
                        >
                            Confirmar e Continuar
                        </NxButton>
                    </div>
                </GlassCard>
            </div>
        </div>
    );
};

export default DocumentExtractionPanel;
